import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Inbox, Clock, CheckCircle, XCircle, ArrowLeft, Eye, Phone, Mail, Calendar, User, MessageSquare, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ASB606Support() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [activeView, setActiveView] = useState('main'); // 'main', 'requests', 'details'
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [statusFilter, setStatusFilter] = useState('new');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Fetch contact requests
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        status: statusFilter
      });
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin/contact-requests?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/contact-requests/stats`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Update request status
  const updateStatus = async (requestId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/contact-requests/${requestId}/status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        }
      );
      
      if (response.ok) {
        fetchRequests();
        fetchStats();
        if (selectedRequest?.id === requestId) {
          setSelectedRequest(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeView === 'requests') {
      fetchRequests();
    }
  }, [activeView, statusFilter, currentPage]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    const statuses = {
      'new': { color: 'bg-blue-500', textAr: 'جديد', textEn: 'New', icon: Clock },
      'in_progress': { color: 'bg-amber-500', textAr: 'قيد المعالجة', textEn: 'In Progress', icon: Loader2 },
      'resolved': { color: 'bg-emerald-500', textAr: 'تم الحل', textEn: 'Resolved', icon: CheckCircle },
      'closed': { color: 'bg-gray-500', textAr: 'مغلق', textEn: 'Closed', icon: XCircle }
    };
    return statuses[status] || statuses['new'];
  };

  return (
    <DashboardLayout
      title="دعم ASB606 ADMIN"
      titleEn="ASB606 ADMIN Support"
      subtitle="إدارة طلبات التواصل من الموقع الرئيسي"
      subtitleEn="Manage contact requests from the main website"
      screenCode="SUP-006"
      backPath="/dashboard?platform=support"
      breadcrumb={[
        { labelAr: 'الدعم والمساعدة', labelEn: 'Support & Help', path: '/dashboard?platform=support' },
        { labelAr: 'ASB606 ADMIN (الدعم)', labelEn: 'ASB606 ADMIN (Support)', path: null }
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main View - Contact Form Button Only */}
        {activeView === 'main' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center">
                <Inbox className="w-10 h-10 text-[#d4af37]" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                {language === 'ar' ? 'فورم تواصل معنا' : 'Contact Us Form'}
              </h2>
              <p className="text-[#808080]">
                {language === 'ar' ? 'إدارة طلبات التواصل الواردة من الصفحة الرئيسية للموقع' : 'Manage contact requests from the main website'}
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10 text-center">
                <div className="text-3xl font-bold text-blue-500">{stats.new}</div>
                <div className="text-sm text-[#808080]">{language === 'ar' ? 'جديد' : 'New'}</div>
              </div>
              <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10 text-center">
                <div className="text-3xl font-bold text-amber-500">{stats.in_progress}</div>
                <div className="text-sm text-[#808080]">{language === 'ar' ? 'قيد المعالجة' : 'In Progress'}</div>
              </div>
              <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10 text-center">
                <div className="text-3xl font-bold text-emerald-500">{stats.resolved}</div>
                <div className="text-sm text-[#808080]">{language === 'ar' ? 'تم الحل' : 'Resolved'}</div>
              </div>
              <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10 text-center">
                <div className="text-3xl font-bold text-gray-500">{stats.closed}</div>
                <div className="text-sm text-[#808080]">{language === 'ar' ? 'مغلق' : 'Closed'}</div>
              </div>
            </div>

            {/* Main Button */}
            <div className="flex justify-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveView('requests')}
                className="bg-gradient-to-r from-[#d4af37] to-[#c9a227] hover:from-[#c9a227] hover:to-[#b8922a] text-black rounded-xl py-4 px-8 text-lg font-bold flex items-center justify-center gap-2 shadow-lg shadow-[#d4af37]/20"
                data-testid="view-requests-btn"
              >
                <Inbox className="h-5 w-5" />
                {language === 'ar' ? 'عرض طلبات التواصل' : 'View Contact Requests'}
                {stats.new > 0 && (
                  <span className="ms-2 px-2 py-0.5 bg-red-500 text-white text-sm rounded-full">
                    {stats.new}
                  </span>
                )}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Requests List View */}
        {activeView === 'requests' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Back Button */}
            <button
              onClick={() => setActiveView('main')}
              className="flex items-center gap-2 text-[#808080] hover:text-[#d4af37] mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </button>

            {/* Status Filter */}
            <div className="flex flex-wrap gap-2 mb-6 p-1 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 w-fit">
              {['new', 'in_progress', 'resolved', 'closed'].map((status) => {
                const info = getStatusInfo(status);
                const StatusIcon = info.icon;
                return (
                  <button
                    key={status}
                    onClick={() => {
                      setStatusFilter(status);
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
                      statusFilter === status 
                        ? `${info.color} text-white shadow-lg` 
                        : 'text-[#71717A] hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <StatusIcon className="h-4 w-4" />
                    {language === 'ar' ? info.textAr : info.textEn}
                    {status === 'new' && stats.new > 0 && (
                      <span className="px-2 py-0.5 bg-white/20 text-xs rounded-full">{stats.new}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Requests List */}
            {loading ? (
              <div className="text-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-[#d4af37] mx-auto" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-16 text-[#808080] bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10">
                <Inbox className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">{language === 'ar' ? 'لا توجد طلبات' : 'No requests'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => {
                  const statusInfo = getStatusInfo(request.status);
                  return (
                    <motion.div
                      key={request.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={() => {
                        setSelectedRequest(request);
                        setActiveView('details');
                      }}
                      className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/50 transition-all cursor-pointer group hover:bg-white/[0.04]"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-bold">{request.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${statusInfo.color} text-white`}>
                              {language === 'ar' ? statusInfo.textAr : statusInfo.textEn}
                            </span>
                          </div>
                          <p className="text-[#808080] text-sm mb-2">{request.subject}</p>
                          <div className="flex items-center gap-4 text-xs text-[#606060]">
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {request.phone}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {request.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(request.created_at)}
                            </span>
                          </div>
                        </div>
                        <Eye className="h-5 w-5 text-[#606060] group-hover:text-[#d4af37] transition-colors" />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  className="border-white/10 text-white"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="text-white">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  className="border-white/10 text-white"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* Request Details View */}
        {activeView === 'details' && selectedRequest && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Back Button */}
            <button
              onClick={() => {
                setActiveView('requests');
                setSelectedRequest(null);
              }}
              className="flex items-center gap-2 text-[#808080] hover:text-[#d4af37] mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {language === 'ar' ? 'رجوع للقائمة' : 'Back to List'}
            </button>

            {/* Request Details Card */}
            <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <User className="h-5 w-5 text-[#d4af37]" />
                  {selectedRequest.name}
                </h2>
                <span className={`px-3 py-1 rounded-full text-sm ${getStatusInfo(selectedRequest.status).color} text-white`}>
                  {language === 'ar' ? getStatusInfo(selectedRequest.status).textAr : getStatusInfo(selectedRequest.status).textEn}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-black/30 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center gap-2 text-[#808080] mb-1">
                    <Phone className="h-4 w-4" />
                    <span className="text-xs">{language === 'ar' ? 'رقم الهاتف' : 'Phone'}</span>
                  </div>
                  <p className="text-white font-medium" dir="ltr">{selectedRequest.phone}</p>
                </div>
                
                <div className="bg-black/30 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center gap-2 text-[#808080] mb-1">
                    <Mail className="h-4 w-4" />
                    <span className="text-xs">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</span>
                  </div>
                  <p className="text-white font-medium">{selectedRequest.email}</p>
                </div>
                
                <div className="bg-black/30 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center gap-2 text-[#808080] mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-xs">{language === 'ar' ? 'تاريخ الإرسال' : 'Sent Date'}</span>
                  </div>
                  <p className="text-white font-medium">{formatDate(selectedRequest.created_at)}</p>
                </div>
                
                <div className="bg-black/30 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center gap-2 text-[#808080] mb-1">
                    <MessageSquare className="h-4 w-4" />
                    <span className="text-xs">{language === 'ar' ? 'الموضوع' : 'Subject'}</span>
                  </div>
                  <p className="text-white font-medium">{selectedRequest.subject}</p>
                </div>
              </div>

              {/* Message */}
              <div className="bg-black/30 rounded-xl p-4 border border-white/5">
                <p className="text-xs text-[#808080] mb-2">{language === 'ar' ? 'الرسالة' : 'Message'}</p>
                <p className="text-white whitespace-pre-wrap">{selectedRequest.message}</p>
              </div>
            </div>

            {/* Status Update */}
            <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10">
              <h3 className="text-lg font-bold text-white mb-4">
                {language === 'ar' ? 'تحديث الحالة' : 'Update Status'}
              </h3>
              <div className="flex flex-wrap gap-3">
                {['new', 'in_progress', 'resolved', 'closed'].map((status) => {
                  const info = getStatusInfo(status);
                  const isActive = selectedRequest.status === status;
                  return (
                    <Button
                      key={status}
                      onClick={() => !isActive && updateStatus(selectedRequest.id, status)}
                      disabled={isActive || updatingStatus}
                      className={`gap-2 ${isActive ? 'opacity-50 cursor-not-allowed' : ''} ${info.color} text-white`}
                    >
                      {updatingStatus ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <info.icon className="h-4 w-4" />
                      )}
                      {language === 'ar' ? info.textAr : info.textEn}
                      {isActive && <CheckCircle className="h-4 w-4" />}
                    </Button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </section>
    </DashboardLayout>
  );
}
